import type { CSSProperties } from 'react';
import { Clock, LayoutGrid, List, MessageCircle, Search, SlidersHorizontal } from 'lucide-react';
import {
  CONTENT_CATEGORY_LABELS,
  CONTENT_DIRECTION_LABELS,
  type ContentCategory,
  type ContentDirection,
} from '@shared/api.interface';
import { CATEGORY_STYLES, getCategoryStyle } from '@/utils/topic-category-style';
import { usePosterImage } from './poster-images';
import type { PosterSlot, PosterTopic } from './poster-plan';
import './xhs-poster.css';

export const POSTER_WIDTH = 1080;
export const POSTER_HEIGHT = 1440;

const CATEGORY_ORDER = Object.keys(CATEGORY_STYLES) as ContentCategory[];

const brandLogoSrc =
  (typeof document !== 'undefined' && document.querySelector('link[rel="icon"]')?.getAttribute('href')) ||
  '/brand-logo.svg';

// html2canvas 画不出省略号，长文字在这里先截断
const clip = (text: string, max: number) => {
  const chars = Array.from(text.trim());
  return chars.length > max ? `${chars.slice(0, max - 1).join('')}…` : chars.join('');
};

const bg = (url: string): CSSProperties => ({ backgroundImage: `url("${url.replace(/"/g, '%22')}")` });

function Cover({ topic, className = '', style }: { topic: PosterTopic; className?: string; style?: CSSProperties }) {
  const url = usePosterImage(topic.cover_image);
  const cat = getCategoryStyle(topic.content_category);
  if (url) {
    return <div className={`xp-cover ${className}`} style={{ ...bg(url), ...style }} />;
  }
  if (url === undefined && topic.cover_image) {
    return <div className={`xp-cover ${className}`} style={style} />;
  }
  return (
    <div className={`xp-cover xp-cover-ph ${className}`} style={{ background: cat.coverBg, color: cat.coverInk, ...style }}>
      <span className="xp-o1" />
      <span className="xp-o2" style={{ background: cat.dot }} />
      <span className="xp-ct">{clip(topic.title, 10)}</span>
    </div>
  );
}

function Preview({ src, className = '', label }: { src: string | null; className?: string; label?: string }) {
  const url = usePosterImage(src);
  return (
    <div className={`xp-preview ${className}`} style={url ? bg(url) : undefined}>
      {label && <span className="xp-label">{label}</span>}
    </div>
  );
}

function CategoryTag({ topic }: { topic: PosterTopic }) {
  const cat = getCategoryStyle(topic.content_category);
  return (
    <span className="xp-tag" style={{ background: cat.tagBg, color: cat.tagFg }}>
      {CONTENT_CATEGORY_LABELS[topic.content_category] || '其他'}
    </span>
  );
}

function GradeTag({ topic }: { topic: PosterTopic }) {
  const low = topic.suitable_grade === 'low';
  return (
    <span className="xp-tag" style={low ? { background: '#e1f2e7', color: '#23704a' } : { background: '#e3eaf8', color: '#2f569a' }}>
      {low ? '低年级' : '高年级'}
    </span>
  );
}

const DIRECTION_STYLES: Record<ContentDirection, { bg: string; fg: string }> = {
  chinese_spirit: { bg: '#F8DFDC', fg: '#8C2420' },
  chinese_wisdom: { bg: '#FBEFD8', fg: '#83560C' },
  chinese_feature: { bg: '#E1EFF7', fg: '#235F86' },
  other: { bg: '#ECE6F8', fg: '#57429A' },
};

/** 网站首页顶部：品牌、搜索、分类按钮（照手机首页） */
function SiteTop({ active }: { active?: ContentCategory }) {
  return (
    <div className="xs-top">
      <div className="xs-hd">
        <img className="xs-logo" src={brandLogoSrc} alt="" />
        <span className="xs-brand">少儿英语说</span>
        <div className="xs-hd-r">
          <span className="xs-kf">
            <MessageCircle size={26} color="#6b5e52" strokeWidth={2} />
            联系客服
          </span>
          <span className="xs-login">登录</span>
        </div>
      </div>
      <div className="xs-srow">
        <div className="xs-search">
          <Search size={30} color="#7c6f62" strokeWidth={2} />
          搜主题名，如：花木兰
        </div>
        <div className="xs-fbtn">
          <SlidersHorizontal size={28} color="#9a4a12" strokeWidth={2.2} />
          筛选
        </div>
      </div>
      <div className="xs-chips">
        <span className={`xs-chip xs-chip-all ${active ? '' : 'xs-chip-on'}`}>全部</span>
        {CATEGORY_ORDER.map((key) => (
          <span key={key} className={`xs-chip ${active === key ? 'xs-chip-on' : ''}`}>
            {CONTENT_CATEGORY_LABELS[key]}
          </span>
        ))}
      </div>
    </div>
  );
}

function CountBar({ total, mode }: { total: number; mode: 'grid' | 'list' }) {
  return (
    <div className="xs-cnt">
      <span className="xs-n">
        共 <b>{total}</b> 个
      </span>
      <div className="xs-tog">
        <span className={mode === 'grid' ? 'xs-tog-on' : ''}>
          <LayoutGrid size={24} color={mode === 'grid' ? '#2e241b' : '#7c6f62'} strokeWidth={2} />
          封面
        </span>
        <span className={mode === 'list' ? 'xs-tog-on' : ''}>
          <List size={24} color={mode === 'list' ? '#2e241b' : '#7c6f62'} strokeWidth={2} />
          列表
        </span>
      </div>
    </div>
  );
}

function MiniHeader() {
  return (
    <div className="xs-mini">
      <img className="xs-logo" src={brandLogoSrc} alt="" />
      <span className="xs-brand">少儿英语说</span>
    </div>
  );
}

/** 首页封面三列的小卡片 */
function Tile({ topic }: { topic: PosterTopic }) {
  return (
    <div className="xs-tile">
      <Cover topic={topic} className="xs-169" />
      <div className="xs-tile-t">{clip(topic.title, 8)}</div>
      <div className="xs-tile-du">
        <Clock size={18} color="#c2410c" strokeWidth={2.4} />
        {topic.estimated_duration}
      </div>
    </div>
  );
}

/** 首页列表模式的一行 */
function ListRow({ topic }: { topic: PosterTopic }) {
  const cat = getCategoryStyle(topic.content_category);
  return (
    <div className="xs-row">
      <Cover topic={topic} className="xs-thumb" />
      <div className="xs-row-bd">
        <div className="xs-row-t">{clip(topic.title, 13)}</div>
        <div className="xs-row-s">
          <span className="xs-dot" style={{ background: cat.dot }} />
          {CONTENT_CATEGORY_LABELS[topic.content_category] || '其他'} · {topic.estimated_duration}
        </div>
      </div>
      <span className="xs-get">领取</span>
    </div>
  );
}

/** 网站电脑版的大卡片：封面 + 时长角标 + 名称 + 分类 / 方向 */
function BigCard({ topic, hero = false, compact = false }: { topic: PosterTopic; hero?: boolean; compact?: boolean }) {
  return (
    <div className={`xs-card ${hero ? 'xs-card-hero' : ''} ${compact ? 'xs-card-compact' : ''}`}>
      <div className="xs-card-cw">
        <Cover topic={topic} className="xs-169" />
        <span className="xs-pill">
          <Clock size={18} color="#ffffff" strokeWidth={2.4} />
          {topic.estimated_duration}
        </span>
      </div>
      <div className="xs-card-bd">
        <div className="xs-card-t">{clip(topic.title, hero ? 16 : 11)}</div>
        <div className="xs-tags">
          <CategoryTag topic={topic} />
          {!compact && <span className="xs-dir">{CONTENT_DIRECTION_LABELS[topic.content_direction]}</span>}
        </div>
        {hero && topic.content_description && <div className="xs-desc">{clip(topic.content_description, 50)}</div>}
      </div>
    </div>
  );
}

function DirectionBand({ direction, count, compact = false }: { direction?: ContentDirection; count: number; compact?: boolean }) {
  const style = DIRECTION_STYLES[direction || 'other'];
  return (
    <div className={`xs-band ${compact ? 'xs-band-compact' : ''}`} style={{ background: style.bg, color: style.fg }}>
      <span className="xs-band-k">少儿英语说 · 内容方向</span>
      <span className="xs-band-v">{direction ? CONTENT_DIRECTION_LABELS[direction] : '精选主题'}</span>
      <span className="xs-band-c">共 {count} 个主题</span>
    </div>
  );
}

function PlayButton() {
  return (
    <span className="xp-play">
      <span className="xp-play-tri" />
    </span>
  );
}

function ResourceList() {
  return (
    <>
      <div className="xp-res-t">包含资源</div>
      <div className="xp-res">
        <div className="xp-res-item">
          <b>演讲背景视频</b>
          <span>原声示范 · 无人声有字幕 · 无人声无字幕</span>
        </div>
        <div className="xp-res-item">
          <b>演讲背景 PPT</b>
          <span>可编辑 · 内含背景音乐</span>
        </div>
      </div>
      <div className="xp-res2">
        <span>中英双语文稿</span>
        <span>示范音频</span>
        <span>背景音乐</span>
      </div>
    </>
  );
}

const RESOURCE_FILES = [
  { ext: 'MP4', color: '#d9782d', name: '原声示范视频' },
  { ext: 'MP4', color: '#d9782d', name: '无人声有字幕视频' },
  { ext: 'MP4', color: '#d9782d', name: '无人声无字幕视频' },
  { ext: 'PPT', color: '#c8553d', name: '演讲背景 PPT（可编辑）' },
  { ext: 'DOC', color: '#3f7cc0', name: '中英双语文稿' },
  { ext: 'MP3', color: '#2f9a6b', name: '示范音频' },
  { ext: 'MP3', color: '#2f9a6b', name: '背景音乐' },
];

interface PosterProps {
  slot: PosterSlot;
  topicsById: Map<string, PosterTopic>;
  allTopics: PosterTopic[];
}

export function Poster({ slot, topicsById, allTopics }: PosterProps) {
  const ts = slot.topicIds.map((id) => topicsById.get(id)).filter((t): t is PosterTopic => Boolean(t));
  const first = ts[0];
  const categoryTotal = slot.category ? allTopics.filter((t) => t.content_category === slot.category).length : allTopics.length;
  const directionTotal = slot.direction ? allTopics.filter((t) => t.content_direction === slot.direction).length : allTopics.length;

  switch (slot.template) {
    case 'A1':
      return (
        <div className="xp xs">
          <SiteTop active={slot.category} />
          <CountBar total={categoryTotal} mode="grid" />
          <div className="xs-g3">
            {ts.map((t) => (
              <Tile key={t.id} topic={t} />
            ))}
          </div>
        </div>
      );
    case 'A2': {
      const active = slot.category || first?.content_category;
      const matched = active ? allTopics.filter((t) => t.content_category === active).length : allTopics.length;
      return (
        <div className="xp xs">
          <SiteTop active={active} />
          <CountBar total={matched} mode="grid" />
          <div className="xs-g3">
            {ts.map((t) => (
              <Tile key={t.id} topic={t} />
            ))}
          </div>
          <div className="xp-dim" />
          <div className="xp-sheet">
            <div className="xp-grab" />
            <div className="xp-sheet-t">筛选主题</div>
            <div className="xp-sec">内容分类</div>
            <div className="xp-fgrid">
              {CATEGORY_ORDER.map((key) => (
                <span key={key} className={`xp-chip ${key === active ? 'xp-chip-on' : ''}`}>
                  {CONTENT_CATEGORY_LABELS[key]}
                </span>
              ))}
            </div>
            <div className="xp-sec">演讲时长</div>
            <div className="xp-fgrid">
              <span className="xp-chip">1分半内</span>
              <span className="xp-chip xp-chip-on">1分半-2分</span>
              <span className="xp-chip">2分-2分半</span>
              <span className="xp-chip">2分半以上</span>
            </div>
            <div className="xp-go">查看 {matched} 个主题</div>
          </div>
        </div>
      );
    }
    case 'A3':
      return (
        <div className="xp xs">
          <SiteTop active={slot.category} />
          <CountBar total={categoryTotal} mode="list" />
          <div className="xs-list">
            {ts.map((t) => (
              <ListRow key={t.id} topic={t} />
            ))}
          </div>
        </div>
      );
    case 'A4':
      return (
        <div className="xp xs">
          <MiniHeader />
          <div className="xs-g2">
            {ts.map((t) => (
              <BigCard key={t.id} topic={t} />
            ))}
          </div>
        </div>
      );
    case 'A5': {
      const [hero, ...rest] = ts;
      return (
        <div className="xp xs">
          <MiniHeader />
          {hero && (
            <div className="xs-hero">
              <BigCard topic={hero} hero />
            </div>
          )}
          <div className="xs-g2">
            {rest.slice(0, 2).map((t) => (
              <BigCard key={t.id} topic={t} />
            ))}
          </div>
        </div>
      );
    }
    case 'B1': {
      if (!first) return <div className="xp" />;
      return (
        <div className="xp xs">
          <div className="xs-g3 xs-g3-bg">
            {b1Backdrop(first, allTopics).map((t) => (
              <Tile key={t.id} topic={t} />
            ))}
          </div>
          <div className="xp-dim" />
          <div className="xp-modal">
            <div className="xp-modal-media">
              <Cover topic={first} className="xs-169" />
              <PlayButton />
              <span className="xp-close">✕</span>
              <span className="xs-vlab">示范视频</span>
            </div>
            <div className="xp-mb">
              <div className="xp-mb-name">{clip(first.title, 14)}</div>
              <div className="xp-row">
                <CategoryTag topic={first} />
                <span className="xs-dir">{CONTENT_DIRECTION_LABELS[first.content_direction]}</span>
                <span className="xs-dir">· {first.estimated_duration}</span>
              </div>
              {first.content_description && <div className="xp-desc">{clip(first.content_description, 56)}</div>}
              <ResourceList />
            </div>
          </div>
        </div>
      );
    }
    case 'B2':
      if (!first) return <div className="xp" />;
      return (
        <div className="xp">
          <div className="xp-b2">
            <div className="xp-row">
              <span className="xp-b2-name">{clip(first.title, 12)}</span>
              <CategoryTag topic={first} />
              <GradeTag topic={first} />
            </div>
            <Preview src={first.ppt_display} className="xp-preview-169" label="PPT" />
            <div className="xp-b2-bottom">
              <div className="xp-panel">
                <span className="xp-label">视频</span>
                <Cover topic={first} />
                <PlayButton />
              </div>
              {first.script_display ? (
                <Preview src={first.script_display} className="xp-panel" label="文稿" />
              ) : (
                <div className="xp-b2-res">
                  <ResourceList />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    case 'C1': {
      const categories = CATEGORY_ORDER.filter((key) => allTopics.some((t) => t.content_category === key));
      return (
        <div className="xp">
          <div className="xp-pad">
            <div>
              <div className="xp-kicker">少儿英语说 · 主题目录</div>
              <div className="xp-big">{categories.length} 大分类</div>
            </div>
            <div className="xp-cats" style={{ gridTemplateRows: `repeat(${Math.ceil(categories.length / 2)}, 1fr)` }}>
              {categories.map((key) => {
                const style = CATEGORY_STYLES[key];
                const list = allTopics.filter((t) => t.content_category === key);
                return (
                  <div key={key} className="xp-cat" style={{ background: style.tagBg, color: style.tagFg }}>
                    <div className="xp-cat-name">
                      <span className="xp-dot" style={{ background: style.dot, width: 24, height: 24 }} />
                      {CONTENT_CATEGORY_LABELS[key]}
                    </div>
                    <div className="xp-cat-count">{list.length} 个主题</div>
                    <div className="xp-cat-covers">
                      {c1Covers(list).map((t) => (
                        <Cover key={t.id} topic={t} className="xs-169" />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }
    case 'C2':
      return (
        <div className="xp">
          <div className="xp-pad" style={{ background: '#f3ece2' }}>
            <div>
              <div className="xp-kicker">演讲背景 PPT</div>
              <div className="xp-big" style={{ fontSize: 56 }}>每个主题都配好了</div>
            </div>
            <div className="xp-pptwall">
              {ts.map((t) => (
                <Preview key={t.id} src={t.ppt_display} className="xp-preview-169" />
              ))}
            </div>
          </div>
        </div>
      );
    case 'C3':
      if (!first) return <div className="xp" />;
      return (
        <div className="xp">
          <div className="xp-c3">
            <div className="xp-c3-paper">
              <Preview src={first.script_display} />
            </div>
            <div className="xp-c3-stamp">
              <Cover topic={first} className="xp-cover-34" />
            </div>
          </div>
        </div>
      );
    case 'C4': {
      const [hero, ...rest] = ts;
      return (
        <div className="xp xs">
          <DirectionBand direction={slot.direction} count={directionTotal} />
          {hero && (
            <div className="xs-hero">
              <BigCard topic={hero} />
            </div>
          )}
          <div className="xs-list xs-list-tight">
            {rest.slice(0, 4).map((t) => (
              <ListRow key={t.id} topic={t} />
            ))}
          </div>
        </div>
      );
    }
    case 'C6':
      return (
        <div className="xp xs">
          <DirectionBand direction={slot.direction} count={directionTotal} compact />
          <div className="xs-g2 xs-g2-tight">
            {ts.map((t) => (
              <BigCard key={t.id} topic={t} compact />
            ))}
          </div>
        </div>
      );
    case 'C5':
      if (!first) return <div className="xp" />;
      return (
        <div className="xp">
          <div className="xp-pad">
            <div className="xp-c5-head">
              <Cover topic={first} className="xp-cover-34" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
                <div className="xp-kicker">一个主题包含</div>
                <div className="xp-c5-name">{clip(first.title, 10)}</div>
                <div className="xp-row">
                  <CategoryTag topic={first} />
                  <GradeTag topic={first} />
                </div>
              </div>
            </div>
            <div className="xp-folder">
              <div className="xp-folder-h">
                <span className="xp-folder-ic" />
                {clip(first.title, 16)}
              </div>
              {RESOURCE_FILES.map((file) => (
                <div key={file.name} className="xp-file">
                  <span className="xp-file-ic" style={{ background: file.color }}>
                    {file.ext}
                  </span>
                  <span className="xp-file-name">
                    {clip(first.title, 10)}-{file.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    default:
      return <div className="xp" />;
  }
}

// B1 背后露出的首页：除了弹窗里这个主题，挑有封面的
function b1Backdrop(first: PosterTopic, allTopics: PosterTopic[]) {
  return allTopics.filter((t) => t.id !== first.id && t.cover_image).slice(0, 12);
}

function c1Covers(list: PosterTopic[]) {
  return [...list.filter((t) => t.cover_image), ...list.filter((t) => !t.cover_image)].slice(0, 2);
}

/** 收集一张图里要用到的图片地址，导出前预加载 */
export function collectPosterImages(slot: PosterSlot, topicsById: Map<string, PosterTopic>, allTopics: PosterTopic[]): string[] {
  const ts = slot.topicIds.map((id) => topicsById.get(id)).filter((t): t is PosterTopic => Boolean(t));
  const urls: Array<string | null> = [];
  ts.forEach((t) => urls.push(t.cover_image, t.ppt_display, t.script_display));
  if (slot.template === 'B1' && ts[0]) {
    b1Backdrop(ts[0], allTopics).forEach((t) => urls.push(t.cover_image));
  }
  if (slot.template === 'C1') {
    CATEGORY_ORDER.forEach((key) => {
      c1Covers(allTopics.filter((t) => t.content_category === key)).forEach((t) => urls.push(t.cover_image));
    });
  }
  return urls.filter((u): u is string => Boolean(u));
}
